import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countries } from "./CountryName";
import {
  GraduationCap,
  MapPin,
  Calendar,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  School,
  Clock,
  Building2,
  History,
  Globe
} from "lucide-react";

const getEmptyEducation = () => ({
  level: "",
  schoolName: "",
  city: "",
  country: "",
  address: "",
  from: "",
  to: "",
});

const EducationForm = () => {
  const [education, setEducation] = useState([getEmptyEducation()]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const formatDateForInput = (date) => (date ? new Date(date).toISOString().split("T")[0] : "");

  useEffect(() => {

    const fetchEducation = async () => {
      setLoading(true);
      try {
        const res = await makeRequestAuth("get", "/user/getEducation");
        if (res?.data?.educations?.length) {
          const formatted = res.data.educations.map(edu => ({
            ...edu,
            from: formatDateForInput(edu.from),
            to: formatDateForInput(edu.to)
          }));
          setEducation(formatted);
          setPreview({ ...res.data, educations: formatted });
        }
      } catch (err) {
        console.error("No history found.");
      } finally {
        setLoading(false);
      }
    };


    fetchEducation();

  }, []);





  const handleChange = (index, field, value) => {
    const updated = [...education];
    updated[index][field] = value;

    if (field === "from" && updated[index].to && value > updated[index].to) {
      updated[index].to = "";
    }
    setEducation(updated);
  };

  const validateEducation = () => {
    let list = [];
    education.forEach((edu, index) => {
      const row = `Education #${index + 1}`;
      if (!edu.level?.trim()) list.push(`${row}: Degree Level is missing`);
      if (!edu.schoolName?.trim()) list.push(`${row}: School Name is missing`);
      if (!edu.from) list.push(`${row}: Start Date is missing`);
      if (!edu.to) list.push(`${row}: End Date is missing`);
      if (edu.from && edu.to && edu.from > edu.to) {
        list.push(`${row}: Start date cannot be after end date`);
      }
    });
    return list;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateEducation();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    try {

      const payload = education.map(({ _id, ...rest }) => ({
        ...rest
      }));

      const res = await makeRequestAuth("post", "/user/createOrUpdateEducation", { educations: payload });
      setPreview(res.data);
      showSuccessToast("Academic profile saved! ✅");
    } catch (err) {
      showErrorToast("Failed to save. Check connection.");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { bg: "bg-amber-100", text: "text-amber-800", icon: <Clock size={14} /> },
    approve: { bg: "bg-emerald-100", text: "text-emerald-800", icon: <CheckCircle2 size={14} /> },
    reject: { bg: "bg-rose-100", text: "text-rose-800", icon: <AlertCircle size={14} /> }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8 font-sans font-normal antialiased">
      <ToastContainer />

      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 pb-6 border-b-2 border-slate-100">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-[#132b45] text-white rounded-2xl shadow-lg">
            <GraduationCap size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-medium text-[#132b45] tracking-tight">Academic History</h1>
            <p className="text-slate-500 font-normal uppercase text-[11px] tracking-wider">Official Education Declaration</p>
          </div>
        </div>
        {preview && (
          <div className={`flex items-center gap-2 px-6 py-2.5 rounded-full border-2 font-medium uppercase text-[11px] tracking-wider ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.bg} ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.text}`}>
            {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon}
            {preview?.status || "In Review"}
          </div>
        )}
      </div>

      {/* ERROR MESSAGE BOX */}
      {errors.length > 0 && (
        <div className="bg-rose-50 border-2 border-rose-200 p-6 rounded-3xl mb-8">
          <p className="text-rose-800 font-medium text-sm uppercase mb-3 flex items-center gap-2">
            <AlertCircle size={18} /> Review Required:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-normal text-rose-600">
            {errors.map((err, i) => <li key={i}>• {err}</li>)}
          </ul>
        </div>
      )}

      {preview && !loading ? (
        /* ================= PREVIEW ================= */
        <div className="p-8 md:p-12 space-y-10">
          <div className="grid grid-cols-1 gap-12 relative">
            {/* TIMELINE DECORATIVE LINE */}
            <div className="absolute left-[2.25rem] top-10 bottom-10 w-0.5 bg-slate-100 hidden lg:block" />

            {preview.educations?.map((edu, i) => (
              <div
                key={edu._id || i}
                className="relative flex flex-col lg:flex-row gap-6 lg:pl-20 group"
              >
                {/* FLOATING ICON */}
                <div className="absolute left-0 top-6 hidden lg:flex h-12 w-12 rounded-2xl bg-white border-2 border-slate-100 text-slate-400 items-center justify-center z-10 shadow-sm group-hover:border-[#132b45] group-hover:text-[#132b45] transition-all duration-300">
                  <School size={22} />
                </div>

                <div className="flex-1 bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm group-hover:shadow-md group-hover:border-indigo-100 transition-all">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 pb-6 border-b border-slate-50">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-[9px] font-black uppercase tracking-[0.15em]">
                          {edu.level || "Qualification"}
                        </span>
                        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-[9px] font-black uppercase tracking-[0.15em]">
                          {edu.country || "International"}
                        </span>
                      </div>
                      <h5 className="font-black text-[#132b45] text-2xl uppercase tracking-tighter">
                        {edu.schoolName}
                      </h5>
                    </div>

                    <div className="flex items-center gap-3 bg-slate-50 px-5 py-3 rounded-2xl border border-slate-100">
                      <div className="text-center">
                        <p className="text-[9px] font-black text-slate-400 uppercase mb-0.5">
                          Joined
                        </p>
                        <p className="text-xs font-bold text-slate-700">
                          {formatDate(edu.from)}
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-slate-300" />
                      <div className="text-center">
                        <p className="text-[9px] font-black text-slate-400 uppercase mb-0.5">
                          Completed
                        </p>
                        <p className="text-xs font-bold text-slate-700">
                          {formatDate(edu.to)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <AdminPreview
                      icon={<Building2 size={18} className="text-blue-500" />}
                      label="Campus City"
                      value={edu.city}
                    />
                    <div className="lg:col-span-2">
                      <AdminPreview
                        icon={<MapPin size={18} className="text-rose-500" />}
                        label="Institutional Address"
                        value={edu.address}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Action */}
          <div className="flex flex-col items-center gap-4 pt-10">
            {preview.status === 'reject' ? (
              <>
                <button
                  onClick={() => setPreview(null)}
                  className="group flex items-center gap-3 bg-[#132b45] text-white px-10 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-black transition-all shadow-xl hover:shadow-2xl"
                >
                  Modify Records <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </>
            ) : (
              <div className="text-center">
              </div>
            )}
          </div>

        </div>
      ) : (
        /* ================= FORM ================= */
        <form onSubmit={handleSubmit} className="space-y-12">
          {education.map((edu, index) => (
            <div key={index} className="bg-white border-2 border-slate-100 rounded-[3rem] p-10 relative shadow-sm hover:border-blue-200 transition-colors">
              <div className="flex justify-between items-center mb-10">
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 bg-[#132b45] text-white flex items-center justify-center rounded-2xl font-medium shadow-xl">{index + 1}</span>
                  <h2 className="font-medium text-[#132b45] uppercase tracking-wider text-xs">Education Details</h2>
                </div>
                {education.length > 1 && (
                  <button type="button" onClick={() => setEducation(education.filter((_, i) => i !== index))} className="text-rose-500 hover:bg-rose-50 p-3 rounded-2xl transition-all">
                    <Trash2 size={24} />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                <InputField label="Degree / Level *" icon={<GraduationCap size={16} />} placeholder="Bachelor of Arts" value={edu.level} onChange={(v) => handleChange(index, "level", v)} />
                <InputField label="School Name *" icon={<School size={16} />} placeholder="Oxford University" value={edu.schoolName} onChange={(v) => handleChange(index, "schoolName", v)} />
                <InputField label="City *" icon={<MapPin size={16} />} placeholder="London" value={edu.city} onChange={(v) => handleChange(index, "city", v)} />

                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-medium text-slate-400 uppercase tracking-wider ml-1 flex items-center gap-2">
                    <Globe size={16} /> Country *
                  </label>
                  <select
                    value={edu.country}
                    onChange={(e) => handleChange(index, "country", e.target.value)}
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 font-normal text-slate-700 outline-none focus:border-[#132b45] transition-all appearance-none cursor-pointer"
                  >
                    <option value="">Choose Country</option>
                    {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <InputField label="Address" icon={<MapPin size={16} />} placeholder="Full campus address" value={edu.address} onChange={(v) => handleChange(index, "address", v)} />
                </div>

                <InputField
                  type="date"
                  label="Start Date *"
                  icon={<Calendar size={16} />}
                  max={new Date().toISOString().split("T")[0]}
                  value={edu.from}
                  onChange={(v) => handleChange(index, "from", v)}
                />

                <InputField
                  type="date"
                  label="End Date *"
                  icon={<Calendar size={16} />}
                  min={edu.from}
                  value={edu.to}
                  onChange={(v) => handleChange(index, "to", v)}
                />
              </div>
            </div>
          ))}

          <div className="flex flex-col md:flex-row items-center gap-6 pt-6">
            <button
              type="button"
              onClick={() => setEducation([...education, getEmptyEducation()])}
              className="w-full md:w-auto px-10 py-5 border-4 border-dashed border-slate-200 rounded-[2rem] text-slate-400 font-medium text-xs uppercase tracking-wider hover:border-[#132b45] hover:text-[#132b45] transition-all"
            >
              + Add Record
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full md:flex-1 bg-[#132b45] text-white py-6 rounded-[2rem] font-medium text-sm uppercase tracking-wider shadow-2xl hover:bg-black transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save Academic Profile"} <ChevronRight size={20} />
            </button>
          </div>
        </form>
      )}

      {/* FULL SCREEN LOADER */}
      {loading && (
        <div className="fixed inset-0 bg-white/90 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
          <div className="w-16 h-16 border-8 border-slate-100 border-t-[#132b45] rounded-full animate-spin mb-4" />
          <p className="font-normal text-[10px] uppercase tracking-widest text-[#132b45]">Saving Records</p>
        </div>
      )}
    </div>
  );
};

/* UI HELPERS */
const InputField = ({ label, value, onChange, type = "text", placeholder, icon, min, max }) => (
  <div className="flex flex-col gap-2">
    <label className="text-[11px] font-medium text-slate-400 uppercase tracking-wider ml-1 flex items-center gap-2">
      {icon} {label}
    </label>
    <input
      type={type}
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 font-normal text-[#132b45] placeholder:text-slate-300 outline-none focus:border-[#132b45] focus:bg-white transition-all shadow-sm"
    />
  </div>
);

// const PreviewItem = ({ label, value }) => (
//   <div>
//     <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">{label}</p>
//     <p className="font-normal text-[#132b45] text-base leading-tight">{value || "—"}</p>
//   </div>
// );


const AdminPreview = ({ icon, label, value }) => (
  <div className="flex items-start gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-50 group-hover:bg-white group-hover:border-indigo-100 transition-all duration-300">
    <div className="mt-1 p-2 bg-white rounded-xl shadow-sm text-slate-400 group-hover:text-indigo-600 transition-colors">
      {icon}
    </div>
    <div className="overflow-hidden">
      <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
        {label}
      </span>
      <span className="font-bold text-[#132b45] text-sm block leading-snug group-hover:text-indigo-950 transition-all">
        {value || "—"}
      </span>
    </div>
  </div>
);

// const PreviewItem = ({ label, value, icon }) => (
//   <div className="group">
//     <div className="flex items-center gap-2 mb-1">
//       {icon}
//       <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">{label}</p>
//     </div>
//     <p className="font-medium text-[#132b45] text-[15px] pl-5 md:pl-0 leading-tight">
//       {value || <span className="text-slate-300 italic font-normal">Not provided</span>}
//     </p>
//   </div>
// );

export default EducationForm;